import '.a{display:flex;}.ssr-plugin'
import '.m{flex-direction:column;}.ssr-plugin'
import '.ab{align-items:stretch;}.ssr-plugin'
import '.f0{gap:0.38rem;}.ssr-plugin'
import '.n{align-items:center;}.ssr-plugin'
import '.i1{padding:0.5rem ;}.ssr-plugin'
import '.es{border-radius:0.75rem;}.ssr-plugin'
import '.ev{font-size:1.125rem;}.ssr-plugin'
import '.g1{height:1.75rem;}.ssr-plugin'
import '.ba{flex-grow:1;}.ssr-plugin'
import '.bd{padding:0px;}.ssr-plugin'
import '.b4{min-width:0px;}.ssr-plugin'
import '.a0{width:100%;}.ssr-plugin'
import '.bb{border:none;}.ssr-plugin'
import '.f1{outline:none;}.ssr-plugin'
import '.f2{background:transparent;}.ssr-plugin'
import '.o{text-align:center;}.ssr-plugin'
import { Style, styled } from 'inlines'
import {
  useCallback,
  KeyboardEvent,
  forwardRef,
  useState,
  useRef,
  KeyboardEventHandler,
  ClipboardEventHandler,
  useEffect,
  useLayoutEffect,
} from 'react'
import { Text } from '../../typography/Text'
import Row from '@app/components/layout/Row'
import useMediaQuery from '@app/util/useMediaQuery'

const textToCode = (text: string = null, codeLength: number): string[] => {
  return (text ?? '')
    .slice(0, codeLength)
    .padEnd(codeLength, '\0')
    .split('')
    .map((char) => (char === '\0' ? '' : char))
}

export type VerificationCodeInputProps = {
  text?: string
  placeholder?: 'numbers'
  codeLength?: number
  label?: string
  disabled?: boolean
  errorMessage?: string
  style?: Style
  setText: (text: string) => void
}

export const VerificationCodeInput = ({
  text,
  placeholder: _placeholder,
  codeLength = 6,
  label,
  disabled,
  errorMessage,
  style,
  setText,
}: VerificationCodeInputProps) => {
  const errorStateOn = !disabled && !!errorMessage

  const isMobile = useMediaQuery()
  const [code, setCode] = useState<string[]>(textToCode(text, codeLength))
  const inputRefs = useRef<Array<HTMLInputElement | null>>([])

  useEffect(() => {
    if (text === '') setCode(textToCode(null, codeLength))
  }, [text, codeLength])

  const setCodeText = (text: string, index: number) => {
    const filtered = text.replace(/\D/g, '').slice(0, 1)
    setCode((prev) => {
      prev[index] = filtered
      return [...prev]
    })
    if (filtered.length === 1 && index + 1 < codeLength) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  const onCodeInputKeyDown = (
    e: KeyboardEvent<HTMLInputElement>,
    index: number,
  ) => {
    if (e.metaKey || e.ctrlKey) {
      return
    } else if (e.key === 'Backspace') {
      if (code[index].length === 0 && index > 0) {
        setCodeText('', index - 1)
        inputRefs.current[index - 1]?.focus()
      }
      return
    } else if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault()
      inputRefs.current[index - 1]?.focus()
      moveCursorToTheEnd(inputRefs.current[index - 1])
    } else if (e.key === 'ArrowRight' && index < codeLength - 1) {
      e.preventDefault()
      inputRefs.current[index + 1]?.focus()
      moveCursorToTheEnd(inputRefs.current[index + 1])
    } else if (e.key.length === 1 && !/\d/.test(e.key)) {
      e.preventDefault()
    } else if (
      /\d/.test(e.key) &&
      code[index].length === 1 &&
      index < codeLength - 1
    ) {
      setCodeText(e.key, index + 1)
      e.preventDefault()
    }
  }

  const moveCursorToTheEnd = (ref: HTMLInputElement | null) => {
    ref.selectionStart = 1
    ref.selectionEnd = 1
  }

  useLayoutEffect(() => {
    if (code.every((c) => c === '')) {
      inputRefs.current[0]?.focus()
    }
  }, [code])

  useEffect(() => {
    setText(code.join(''))
  }, [code, setText])

  return (
    <styled.div className="a m ab f0"
      style={{
        '&:has(:disabled)': {
          cursor: 'not-allowed',
        },
        ...style,
      }}
    >
      {label && (
        <Text.Medium style={{ color: 'var(--shades-blue-500)' }}>
          {label}
        </Text.Medium>
      )}
      <Row gap={isMobile ? '0.5rem' : '1rem'} style={{}}>
        {Array.from({ length: codeLength }, (v, k) => k + 1).map((i) => {
          const index = i - 1
          return (
            <SymbolInput
              key={index}
              text={code[index]}
              placeholder={`${i}`}
              errorStateOn={false}
              disabled={disabled}
              setText={(text) => {
                setCodeText(text, index)
              }}
              onKeyDown={(e) => {
                onCodeInputKeyDown(e, index)
              }}
              onPaste={(e) => {
                e.preventDefault()
                const pastedText = e.clipboardData
                  .getData('text/plain')
                  .replace(/\D/g, '')
                  .slice(0, codeLength)
                setCode(textToCode(pastedText, codeLength))
                setText(pastedText)
              }}
              ref={(el) => {
                inputRefs.current[index] = el
              }}
            />
          )
        })}
      </Row>
      {errorStateOn && (
        <Text.ExtraSmall style={{ color: 'var(--shades-red-600)' }}>
          {errorMessage}
        </Text.ExtraSmall>
      )}
    </styled.div>
  )
}

type SymbolInputProps = {
  text: string
  placeholder: string
  errorStateOn: boolean
  disabled: boolean
  setText: (text: string) => void
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>
  onPaste?: ClipboardEventHandler<HTMLInputElement>
}

const SymbolInput = forwardRef<HTMLInputElement, SymbolInputProps>(
  (
    {
      text,
      placeholder,
      errorStateOn,
      disabled,
      setText,
      onKeyDown,
      onPaste,
    }: SymbolInputProps,
    ref,
  ) => {
    const border = useCallback(
      (color: string) =>
        `1px solid var(${errorStateOn ? '--primary-red' : color})`,
      [errorStateOn],
    )

    return (
      <styled.div className="a n i1 es"
        style={{
          border: border('--shades-blue-300'),
          '&:hover': {
            border: border('--shades-blue-500'),
          },
          '&:focus-within': {
            border: border('--primary-blue'),
          },
          '&:has(:disabled)': {
            border: 'var(--gray-border)',
            background: 'var(--gray-100)',
          },
          background: errorStateOn
            ? 'var(--shades-red-100)'
            : 'var(--shades-white)',
        }}
      >
        <styled.input className="ev g1 ba bd b4 a0 bb f1 f2 o"
          ref={ref}
          placeholder={placeholder}
          value={text}
          inputMode="numeric"
          pattern="[0-9]*"
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
          disabled={disabled}
          maxLength={1}
          onPaste={onPaste}
          style={{
            '&::placeholder': {
              color: errorStateOn ? 'var(--shades-red-400)' : 'var(--gray-300)',
              userSelect: 'none',
            },
            '&:disabled': {
              cursor: 'not-allowed',
            },
          }}
        ></styled.input>
      </styled.div>
    )
  },
)

SymbolInput.displayName = 'SymbolInput'
